import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import {
  UserService,
  ExternalURLService,
  MainEntitiesProvider,
  UserAuth,
  BulletinPopupComponent,
  BulletinService,
  ApiService
} from '@nrwl/common-lib';

import { Globals } from '../../authentication.globals';

@Component({
  selector: 'nrwl-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit, OnDestroy {
  hide: boolean;
  isLoading = false;
  errorMsg: string;
  title: string;
  formParam = {
    username: '',
    password: ''
  };
  params: any; //eslint-disable-line
  redirectUrl: string;
  navigateUrl: string;
  brandName = '';
  isLoadingUIConfig: boolean;
  dynamicBranding = false;
  //eslint-disable-next-line
  additionalParams: any;
  isEmbed = false;
  isTokenValid = false;
  isValidPermission = true;
  openIdItems = [];
  openIDButtonPressed = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly router: Router,
    readonly globals: Globals,
    private readonly userService: UserService,
    private route: ActivatedRoute,
    private readonly urlService: ExternalURLService,
    readonly mainEntities: MainEntitiesProvider,
    private userAuth: UserAuth,
    private http: HttpClient,
    private dialog: MatDialog,
    private bulletinService: BulletinService,
    private apiService: ApiService
  ) {
    this.hide = true;
    this.isLoadingUIConfig = true;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  signinForm = new UntypedFormGroup({
    username: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(254)
    ]),
    password: new UntypedFormControl(
      {
        value: '',
        disabled: false
      },
      [Validators.required, Validators.minLength(1), Validators.maxLength(254)]
    )
  });

  ngOnInit() {
    this.loadBrandingEvents();
    this.getLoginProviders();
    if (this.route.snapshot.queryParams['redirect-url']) {
      this.redirectUrl = this.route.snapshot.queryParams['redirect-url'];
    } else if (location.search.includes('redirect-url=')) {
      this.redirectUrl = location.search.split('redirect-url=')[1].split('&')[0];
    }
    if (this.route.snapshot.queryParams['additionalParams']) {
      this.additionalParams = this.route.snapshot.queryParams['additionalParams'];
    }
    this.navigateUrl = this.route.snapshot.queryParams['navigateURL'];
    const currentParams = this.route.snapshot.queryParams;
    const returnUrlParam = this.mergeIntoUrlEncodedParam(currentParams);
    if (returnUrlParam) {
      const routeParams: NavigationExtras = { queryParams: {} };
      routeParams.queryParams['returnUrl'] = returnUrlParam;
      this.router.navigate(['signin'], routeParams);
    }
    this.params = { router: this.router, userService: this.userService };
    this.userService
      .loadSettings$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {},
        error => {}, //eslint-disable-line
        () => {}
      );
    if (this.route.snapshot.queryParams['returnUrl']) {
      const urlItems = this.urlService.splitReturnUrl(this.route.snapshot.queryParams['returnUrl']);
      this.checkTokenBasedURL(urlItems);
    }

    const authToken = this.userService.getCookie(this.userAuth.COOKIE_AUTH);
    if (authToken) {
      this.validateToken({ token: authToken });
    } else {
      this.isTokenValid = false;
    }
  }

  getLoginProviders(): void {
    this.userService
      .getLoginProviders$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.status === 'success' && response.data.includes('openid')) {
          this.getOpenIdProviders();
        }
      });
  }

  getOpenIdProviders(): void {
    this.userService
      .getOpenIdLoginProviders$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.openIdItems = response;
      });
  }

  //eslint-disable-next-line
  checkTokenBasedURL(urlItems: any): void {
    const token = urlItems.token;
    this.isEmbed = urlItems.embed || false;
    if (token) {
      this.validateToken({ token: token });
    }
  }

  //eslint-disable-next-line
  validateToken(response: any): void {
    const userService = this.params.userService;
    userService._setHeaderToken(response);
    const username = 'me';
    this.isLoading = true;
    if (username) {
      //api to check whether token is valid or not
      userService
        ._getCurrentUser$(username)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          () => {
            this.isLoading = false;
            this.isTokenValid = true;
            this._setUserObject(response);
          },
          () => {
            this.isLoading = false;
            this.isTokenValid = false;
          },
          () => {}
        );
    } else {
      this.isTokenValid = false;
    }
  }

  loadBrandingEvents(): void {
    this.userService.brandingEvents.pipe(takeUntil(this.unsubscribe)).subscribe(event => {
      if (event?.dynamicBranding) {
        this.dynamicBranding = true;
      }
      if (this.dynamicBranding && event?.brandName) {
        this.brandName = this.userService.getBrandingName();
      }
    });
  }

  //eslint-disable-next-line
  loginFn(): any {
    if (!this.openIDButtonPressed) {
      this.errorMsg = null;
      this.isLoading = true;
      this.apiService.removeHeader('Authorization');
      this.userService.removeCookies();
      this.userService
        .getUser$(
          JSON.stringify({
            username: this.signinForm.getRawValue().username,
            password: this.signinForm.value.password
          })
        )
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            this._setUserObject(response.success);
            this.isLoading = false;
          },
          error => {
            this.isLoading = false;
            this.errorMsg = this.getErrorMsg(error.status, get(error, 'error.errors[0]', null));
          },
          () => {}
        );
    }
  }

  // eslint-disable-next-line
  _setUserObject(response: any): void {
    const userService = this.params.userService;
    userService._setUser(response);
    userService._pushEvent('login');
    const username = get(this.userAuth, 'data.USER_ID', '');
    userService._getCurrentUser$(username);
    this.openBulletinPopup(response.token);
  }

  //eslint-disable-next-line
  _getError(selector: any, msg: any): any {
    return this.signinForm.get(selector).hasError('required')
      ? msg
      : this.signinForm.get(selector).hasError('minlength')
      ? this.globals.SHORT_ERR
      : this.signinForm.get(selector).hasError('maxlength')
      ? this.globals.LONG_ERR
      : '';
  }

  //eslint-disable-next-line
  getErrorMessageUsername(): any {
    return this._getError('username', this.globals.USERNAME_HT);
  }

  //eslint-disable-next-line
  getErrorMessagePassword(): any {
    return this._getError('password', this.globals.PASSWORD_HT);
  }

  clearUsername(): void {
    this.signinForm.get('username').enable();
    this.signinForm.get('username').setValue(null);
    this.signinForm.get('password').setValue(null);
    this.errorMsg = null;
  }

  //eslint-disable-next-line
  getErrorMsg(code: any, errorMsg?: any): any {
    if (errorMsg) {
      return errorMsg ? errorMsg : `${this.globals.ERR_MSG} ("${code})"`;
    }
    const msg = this.globals.HTTP_ERRORS[code];
    return msg ? msg : `${this.globals.ERR_MSG} ("${code})"`;
  }

  //eslint-disable-next-line
  async redirectToMainPage(token: string) {
    if (this.redirectUrl) {
      let url =
        decodeURIComponent(this.redirectUrl) +
        (this.redirectUrl.includes('localhost') ? '?token=' + token : '');
      if (this.additionalParams) {
        url = `${url}${url.includes('?') ? '&' : '?'}additionalParams=${this.additionalParams}`;
      }
      location.href = url;
    } else if (this.navigateUrl) {
      this.router.navigate([this.navigateUrl]);
    } else {
      if (!this.urlService.hasRecognizableParams()) {
        this.router.navigate(['landing-page']);
        await this.mainEntities.reloadEntities();
      } else {
        // each sign in will proceed only when entities cache is fully loaded.
        await this.urlService.redirectBasedOnURL();
        await this.mainEntities.reloadEntities();
      }
    }
  }

  //eslint-disable-next-line
  mergeIntoUrlEncodedParam(currentParams: any): any {
    let returnUrlParam = '';
    if (currentParams['reqFrom']) {
      returnUrlParam += `reqFrom=${currentParams['reqFrom']}`;
      if (currentParams['view']) {
        returnUrlParam += `&view=${currentParams['view']}`;
      }
      if (currentParams['type']) {
        returnUrlParam += `&type=${currentParams['type']}`;
      }
      if (currentParams['startTime']) {
        returnUrlParam += `&startTime=${currentParams['startTime']}`;
      }
      if (currentParams['endTime']) {
        returnUrlParam += `&endTime=${currentParams['endTime']}`;
      }
      if (currentParams['anomaly']) {
        returnUrlParam += `&anomaly=${currentParams['anomaly']}`;
      }
    }
    return returnUrlParam;
  }

  openBulletinPopup(token: string): void {
    this.isLoading = true;
    this.bulletinService.getBulletinConfiguration$().subscribe(
      response => {
        this.isLoading = false;
        if (
          !this.userService.isFusionSsoProject() &&
          response?.message &&
          response?.displayBulletin
        ) {
          const bulletinDialogRef = this.dialog.open(BulletinPopupComponent, {
            data: response,
            width: '700px',
            disableClose: true
          });
          bulletinDialogRef.afterClosed().subscribe(response => {
            if (response === 'agree') {
              this.redirectToMainPage(token);
            } else {
              this.userService.removeUser();
              window.location.reload();
            }
          });
        } else {
          this.redirectToMainPage(token);
        }
      },
      () => {
        this.isLoading = false;
        this.redirectToMainPage(token);
      }
    );
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  //eslint-disable-next-line
  onOpenIdLogin(providers: any) {
    if (providers && providers.loginUrl) {
      this.openIDButtonPressed = true;
      location.href = providers.loginUrl;
    }
  }
}
